import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  CustomerTypeSegmentation,
  CustomerTypeStats,
  LeadSourceStat,
  VendorStat,
} from "../../../../types/onboardingAnalytics";
import { LeadSource } from "../../../../types/onboarding";
import {
  LEAD_SOURCE_LABELS,
  leadSourceLabel,
} from "../../../../constants/contractSigning";
import {
  formatCount,
  formatDays,
  formatPercent,
  hasMeasurement,
  humaniseSegment,
} from "../../../../utils/analyticsFormat";

const NO_DATA = <span className="text-500">No data yet</span>;

/** Lead sources the backend invents have no label map, so they are humanised. */
const sourceLabel = (source: string): string =>
  source in LEAD_SOURCE_LABELS
    ? leadSourceLabel(source as LeadSource)
    : humaniseSegment(source);

export const LeadSourcePanel: React.FC<{ segments: LeadSourceStat[] }> = ({
  segments,
}) => (
  <DataTable
    value={segments}
    dataKey="source"
    className="p-datatable-sm"
    aria-label="Prospects by lead source"
  >
    <Column header="Lead source" body={(s: LeadSourceStat) => sourceLabel(s.source)} />
    <Column
      header="Prospects"
      body={(s: LeadSourceStat) => formatCount(s.count)}
      style={{ width: "8rem" }}
    />
    <Column
      header="Share"
      // Nothing rather than 0% where there is no share to state.
      body={(s: LeadSourceStat) => formatPercent(s.percentage) ?? "—"}
      style={{ width: "8rem" }}
    />
  </DataTable>
);

interface TypeRow extends CustomerTypeStats {
  label: string;
}

/**
 * Private vs business.
 *
 * The API returns an object with two fixed keys here, not an array like its two
 * sibling segmentations — flattened into rows for display. A type with no
 * prospects has no duration and no conversion to state, so those cells say so
 * rather than showing the `0.0` the API sends for "nothing to average".
 */
export const CustomerTypePanel: React.FC<{
  segmentation: CustomerTypeSegmentation;
}> = ({ segmentation }) => {
  const rows: TypeRow[] = [
    { label: "Private", ...segmentation.privateCustomers },
    { label: "Business", ...segmentation.businessCustomers },
  ];

  return (
    <DataTable
      value={rows}
      dataKey="label"
      className="p-datatable-sm"
      aria-label="Onboarding by customer type"
    >
      <Column field="label" header="Customer type" />
      <Column
        header="Prospects"
        body={(r: TypeRow) => formatCount(r.count)}
        style={{ width: "8rem" }}
      />
      <Column
        header="Average onboarding"
        body={(r: TypeRow) => formatDays(r.avgDurationDays, r.count) ?? NO_DATA}
        style={{ width: "12rem" }}
      />
      <Column
        header="Conversion"
        body={(r: TypeRow) =>
          hasMeasurement(r.count) ? formatPercent(r.conversionRatePct) ?? NO_DATA : NO_DATA
        }
        style={{ width: "9rem" }}
      />
    </DataTable>
  );
};

/**
 * Hardware vendors, measured from credentials sent to the station going public —
 * i.e. how long the customer's own setup takes on that hardware.
 */
export const VendorPanel: React.FC<{ vendors: VendorStat[] }> = ({
  vendors,
}) => (
  <DataTable
    value={vendors}
    dataKey="vendor"
    className="p-datatable-sm"
    aria-label="Setup time by vendor"
  >
    <Column
      header="Vendor"
      body={(v: VendorStat) => humaniseSegment(v.vendor)}
    />
    <Column
      header="Prospects"
      body={(v: VendorStat) => formatCount(v.count)}
      style={{ width: "8rem" }}
    />
    <Column
      header="Average setup"
      body={(v: VendorStat) => formatDays(v.avgSetupDays, v.count) ?? NO_DATA}
      style={{ width: "11rem" }}
    />
  </DataTable>
);
